import React, { useState } from 'react';
import styled from 'styled-components';
import { colors } from '../../../globalStyles';
import {
  AssessmentFlagKey,
  FlagRiskScaleLevel,
  useLatestAssessmentIndexForUserQuery,
} from '../../../graphQL';
import { useDrilldownContext } from '../helpers';
import { Styles } from '../styles';
import { AssessmentFlags } from '../Widgets/AssessmentFlags';

import { SaiStudentFlag } from '../Widgets/saiStudentDetail.types';
import { humanizeEnumValue } from '../../../utils/humanizeEnumValue';
import { AssessmentInsightsModal } from '../Widgets/AssessmentInsights';
import { AssessmentIndexWidget } from '../Widgets/AssessmentIndex/AssessmentIndexWidget';
import { LoadingWidget } from '../Widgets/Loading';
import { TriageHeader } from './TriageHeader';

// Names that differ from what humanizing the key gives, for one of two reasons. A flag named for
// the healthy pole fires on that pole's absence, so its name carries the polarity rather than
// leaving a reader to infer it, and Financial Stress is what product's committed scenario sheet
// calls that flag. The endpoint resolves these for the detail page; this read serves plain keys,
// so they are named here and go when this tab does.
const LEGACY_DISPLAY_NAMES: Record<string, string> = {
  FinancialStrain: 'Financial Stress',
  AcademicConfidence: 'Low Academic Confidence',
  GoalCommitment: 'Low Goal Commitment',
  SenseOfBelonging: 'Low Sense of Belonging',
};

export const BeaconTab = () => {
  const { user } = useDrilldownContext();
  const [insightsOpen, setInsightsOpen] = useState(false);
  const { data, loading, error } = useLatestAssessmentIndexForUserQuery({
    variables: { userId: user.id },
  });

  if (loading) {
    return <LoadingWidget />;
  }

  if (error) {
    return (
      <Styles.widget role="alert">
        Unable to load the student&apos;s assessment index.
      </Styles.widget>
    );
  }

  if (!data?.latestAssessmentIndexForUser) {
    return <Styles.widget>No assessment index is available for this student.</Styles.widget>;
  }

  const index = data.latestAssessmentIndexForUser;

  // This read declares its flag keys and severity levels as plain strings while the widgets are
  // typed on the enums, so each is asserted rather than checked here: the demo backend behind this
  // query guarantees no membership, and an unrecognized value degrades to a raw label downstream.
  // The read also carries none of a flag's copy or index contribution, so its rows are names and
  // severities alone, with no expandable detail and no contribution badges. It serves no per-row
  // identity either, so position stands in for the id the widgets key rows on.
  const flags: SaiStudentFlag[] = index.flags
    .filter(flag => flag.flagKey !== AssessmentFlagKey.ComplexNeedsAlert)
    .map((flag, position) => ({
      id: position,
      flagKey: flag.flagKey as AssessmentFlagKey,
      flagRiskScaleLevel: flag.flagRiskScaleLevel as FlagRiskScaleLevel,
      displayName: LEGACY_DISPLAY_NAMES[flag.flagKey] ?? humanizeEnumValue(flag.flagKey),
      collapsedPhrase: null,
      whatItMeans: null,
      sourceQuestions: [],
      sourceInstrument: null,
      wellbeingDimensions: [],
      persistenceDimensions: [],
    }));

  // The widgets read the index scores in the shape the detail endpoint serves them.
  const indices = {
    wellbeingScore: index.wellbeingIndex.score,
    wellbeingDimensions: index.wellbeingIndex.dimensions,
    persistenceScore: index.persistenceIndex.score,
    persistenceDimensions: index.persistenceIndex.dimensions,
  };

  return (
    <BeaconLayoutDiv>
      <TriageHeader index={index} onOpenInsights={() => setInsightsOpen(true)} />
      <AssessmentIndexWidget indices={indices} flags={flags} />
      <FlagsWidget>
        <AssessmentFlags flags={flags} hasComplexNeeds={index.hasComplexNeeds} minWidth="560px" />
      </FlagsWidget>
      <AssessmentInsightsModal
        aiSummary={index.aiSummary}
        isOpen={insightsOpen}
        onClose={() => setInsightsOpen(false)}
      />
    </BeaconLayoutDiv>
  );
};

// The card the rest of this page's blocks have. The modal draws its own chrome and does not use it.
const FlagsWidget = styled(Styles.widget)`
  padding: 18px 18px 20px;
  margin-bottom: 0;
  min-height: 260px;
  border: 1px solid ${colors.secondaryBorder};
  border-radius: 12px;
`;

const BeaconLayoutDiv = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;
